/**
 * EntityDocumentsTab — reusable Documents tab embedded into
 * ClientDetail / BrandDetail / ProjectDetail.
 *
 * Mirrors EntityDiagramsTab shape.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Plus, ExternalLink, Loader2 } from "lucide-react";

export function EntityDocumentsTab({ entityType, entityId, entityName }) {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (!activeWorkspaceId || !entityId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = { [`${entityType}_id`]: entityId };
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/documents`, { params });
        if (!cancel) setItems(data.items || []);
      } catch (e) { if (!cancel) toast.error(apiErrorMessage(e, "Failed to load documents")); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [activeWorkspaceId, entityType, entityId]);

  const create = async () => {
    if (!title.trim()) return toast.error("Enter a title");
    setCreating(true);
    try {
      const body = {
        title: title.trim(),
        scope: entityType,
        [`${entityType}_id`]: entityId,
      };
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/documents`, body);
      nav(`/app/documents/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-3" data-testid={`${entityType}-documents-tab`}>
      <div className="flex items-end gap-2 border p-3 bg-neutral-50">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">New document for {entityName}</div>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Meeting notes · Client brief · Playbook" className="h-9 rounded-none mt-1" data-testid="entity-documents-title" />
        </div>
        <Button className="rounded-none h-9" onClick={create} disabled={creating || !title.trim()} data-testid="entity-documents-create">
          {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create
        </Button>
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading documents…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground border p-6 text-center">
          <FileText className="w-6 h-6 mx-auto mb-2" />
          No documents yet for this {entityType}. Create one above.
        </div>
      ) : (
        <div className="border">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-muted-foreground border-b p-2 bg-neutral-50">
            <div className="col-span-7">Title</div>
            <div className="col-span-2">Scope</div>
            <div className="col-span-3 text-right">Words · Updated</div>
          </div>
          {items.map((d) => (
            <button
              key={d.id}
              onClick={() => nav(`/app/documents/${d.id}`)}
              className="w-full text-left grid grid-cols-12 items-center p-2 text-xs border-b hover:bg-neutral-50"
              data-testid={`entity-document-row-${d.id}`}
            >
              <div className="col-span-7 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                <div>
                  <div className="font-medium">{d.title}</div>
                  {d.body_text && <div className="text-[10px] text-muted-foreground truncate">{d.body_text}</div>}
                </div>
              </div>
              <div className="col-span-2 text-[10px] uppercase text-muted-foreground">{d.scope}</div>
              <div className="col-span-3 text-right text-[10px] text-muted-foreground flex items-center justify-end gap-1">
                {d.word_count || 0} · {(d.updated_at || "").slice(0, 10)}
                <ExternalLink className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
